import type { IntrinsicElementAttributes } from 'vue'
// The following types are auto-generated from the stylesheet fw.css. This is our source of truth.
import type { Ui, Align, Color, Font, Layout, Shape, Justify } from './generated/fw'
import type { Trim } from 'type-fest'

export { type Align, type Color, type Font, fw, type Layout, type Shape, ui, type Variable } from './generated/fw'

type Attributes = Partial<Ui> & HtmlAttributes

type ToString<T extends string | undefined> = T extends string ? `${T} ` : ''

type EmptyIsUndefined<T extends string> = T extends '' ? undefined : T

/**
 * Make attributes conform to the funkwhale design system
 *
 * 1. `aria-*`: Use `toAttributes` to convert from Vue-friendly (true/false) to Html standard ('' / 'false')
 * @see toHtml
 * 2. `class`: Build union of valid class strings from components `color`, `layout` etc.
 * 3. other Html attributes: Narrows the type to useful values
 * 4. `ui-*`: Narrows the type (e.g. '16' | '32')
 *
 * @example:
 * ```ts
 * defineProps<WithAttributes<{
 *   'aria-current'?: 'page' // 1
 *   class: {
 *     layout?: 'layout-row'
 *   } // 2
 *   disabled?: boolean // 3
 *   uiShrinkable?: boolean // 4
 * }>>()
 * ```
 *
 * @example "No class allowed": `class: object`
 *
 * @see fw.css for class and attribute implementations
 * @see toHtml for converting Vue-friendly props to Html attributes
 *
 * @reference https://design.funkwhale.audio/#/workspace/582e7be1-0cc7-11ed-87a1-ae44a720651d/e3a00150-0f5e-11ed-adb9-fff9e854a67c?page-id=63cce0e0-1a5f-11ed-8551-a35b3c702efa
 */
export type WithAttributes<T extends ({
  class: {
    align?: Align
    color?: Color
    font?: Font
    justify?: Justify
    layout?: Layout
    shape?: Shape
  }
} & Partial<Attributes>)> = Omit<
  // Custom props
  T,
  'class'
> & {
  // Class string
  class?: EmptyIsUndefined<Trim<`${
        ToString<T['class']['align']>
      }${
        ToString<T['class']['color']>
      }${
        ToString<T['class']['font']>
      }${
        ToString<T['class']['justify']>
    }${
        ToString<T['class']['layout']>
      }${
        ToString<T['class']['shape']>
    }`>>
}

/**
 * Common ARIA attributes for UI components. See the links for tips on accessible usage.
 */
export type HtmlAttributes = {
  /**
   * Indicates whether an element is being updated in the background and will not respond to user interactions.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-busy
   */
  ariaBusy?: 'true' | 'false' | boolean

  /**
   * Identifies the element (or elements) whose contents or presence are controlled by the current element.
   * (Pass the ID of the controlled element).
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-controls
   */
  ariaControls?: string

  /**
   * Indicates the element that is visually and semantically current when the element is part of a set of related elements.
   * Can be a boolean or a specific string value.
   * - `true`: Current.
   * - `'page'`: Current page.
   * - `'step'`: Current step in a multi-step process.
   * - `'location'`: Current location within an environment or context.
   * - `'date'`: Current date.
   * - `'time'`: Current time.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-current
   */
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false' | boolean

  /**
   * Identifies the element (or elements) that describes the object.
   * (Pass the ID of the description element).
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-describedby
   */
  ariaDescribedby?: string

  /**
   * Indicates whether the element is exposed to an accessibility API.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-hidden
   */
  ariaHidden?: boolean

  /**
   * Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-haspopup
   */
  ariaHaspopup?: boolean | 'dialog' | 'menu' | 'listbox' | 'tree' | 'grid'

  /**
   * Defines an element's number or position in the current set of listitems or treeitems.
   * Use to implement Tabs, Menu items etc.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-posinset
   */
  ariaPosinset?: number

  /**
   * Defines the number of items in the current set of listitems or treeitems.
   * Use to implement Tabs, Menu items etc.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-setsize
   */
  ariaSetsize?: number

  /**
   * Indicates that the element is not available for interaction.
   * Use only if keeping the disabled control visible is important to maintain layout consistency.
   *
   * Do: Use `disabled` for an option in a group or menu
   * Don't: For a standalone button or link, simply hide it when it does not function.
   */
  disabled?: boolean

  /**
   * Defines the element's role in the accessibility tree.
   *
   * Only use when the default role of a semantic element is not enough.
   */
  role?: string

  /**
   * Controls keyboard focus behavior.
   * - `0`: Focusable in sequential keyboard navigation.
   * - `-1`: Focusable programmatically but not via keyboard.
   */
  tabindex?: '0' | '-1'
} & Labeled & Activated

export type Activated = {

  /**
   * Indicates the current "selected" state of various widgets.
   * Use to implement Tabs, Select options, and Grid cells.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-selected
   */
  ariaSelected?: 'true' | 'false'

  /**
   * Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed.
   * Use to implement Accordions, Menus, and Comboboxes.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-expanded
   */
  ariaExpanded?: 'true' | 'false' | boolean

  /**
   * Indicates the current "pressed" state of toggle buttons and makes the button a toggle.
   * - `true`: Pressed.
   * - `false`: Not pressed.
   * - `undefined`: Not a toggle.
   * Use to implement 2-state toolbar buttons.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-pressed
   */
  ariaPressed?: 'true' | 'false' | boolean
}

export type Labeled = {

  /**
   * Defines a string value that labels the current element.
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-label
   */
  ariaLabel?: string
} | {
  /**
   * Identifies the element (or elements) that labels the current element.
   * (Pass the ID of the label element).
   * @see https://www.w3.org/TR/wai-aria-1.2/#aria-labelledby
   */
  ariaLabelledby?: string
}

/**
 * @param v
 * @returns 'false' if false, 'true' if true, else undefined
 */
const bool = (v: boolean | undefined) =>
  v === false ? 'false'
    : v === true ? 'true'
      : undefined

/**
 * @param val
 * @returns nonempty string, else undefined
 */
const str = <T extends string>(val: T | undefined) =>
  val || undefined

/**
 * @param v
 * @returns 'false' if 'false', 'true' if 'true', 'true' if '' else undefined
 */
const ternary = (v: '' | 'true' | 'false') =>
  v === '' ? 'true' : v

/**
 * - `true`, `'true'`, `''` -> `'true'`
 * - `false`, `'false'` -> `'false'`
 * - `undefined` -> `undefined`
 * @param v
 */
const ternaryOrBoolean = (v: '' | 'true' | 'false' | boolean) =>
  v === '' || v === true || v === 'true' ? 'true' : v === 'false' ? 'false' : undefined

type RelaxUnion<T> = {
  [K in T extends any ? keyof T : never]?: NonNullable<
    T extends any ? (K extends keyof T ? T[K] : never) : never
  >
}

/**
 * Transforms Vue props into strict HTML ARIA attributes
 *
 * @param props
 * @example `v-bind="toHtml(props)"`
 *
 * Specify the Html element type:
 * @example `v-bind="toHtml<'button'>({ ...props, disabled: true })"`
 */
export const toHtml = <E extends keyof IntrinsicElementAttributes>(props: Partial<RelaxUnion<HtmlAttributes>>): IntrinsicElementAttributes[E] => ({
  ...(props.disabled ? { disabled: true } : {}),
  ...(props.role ? { role: str(props.role) } : {}),
  ...(props.tabindex !== undefined ? { tabindex: props.tabindex } : {}),
  // Kebab-case keys so Vue always patches these as attributes (never as ARIAMixin
  // DOM properties): attribute patches can be removed reliably and collide (and thus
  // deduplicate) with other writers of the same attribute, e.g. RouterLink's internal
  // `aria-current` handling.
  ...(props.ariaBusy !== undefined ? { 'aria-busy': ternaryOrBoolean(props.ariaBusy) } : {}),
  ...(props.ariaControls !== undefined ? { 'aria-controls': str(props.ariaControls) } : {}),
  ...(props.ariaCurrent !== undefined && props.ariaCurrent !== false ? {
    'aria-current': props.ariaCurrent === true ? 'true' : props.ariaCurrent
  } : {}),
  ...(props.ariaDescribedby !== undefined ? { 'aria-describedby': str(props.ariaDescribedby) } : {}),
  ...(props.ariaExpanded !== undefined ? { 'aria-expanded': ternaryOrBoolean(props.ariaExpanded) } : {}),
  ...(props.ariaHaspopup !== undefined ? { 'aria-haspopup': props.ariaHaspopup === true ? 'true'
    : props.ariaHaspopup === false ? 'false'
      : props.ariaHaspopup
  } : {}),
  ...(props.ariaHidden !== undefined ? { 'aria-hidden': bool(props.ariaHidden) } : {}),
  ...(props.ariaLabel !== undefined ? { 'aria-label': str(props.ariaLabel) } : {}),
  ...(props.ariaLabelledby !== undefined ? { 'aria-labelledby': str(props.ariaLabelledby) } : {}),
  ...(props.ariaPosinset !== undefined ? { 'aria-posinset': props.ariaPosinset } : {}),
  ...(props.ariaPressed !== undefined ? { 'aria-pressed': ternaryOrBoolean(props.ariaPressed) } : {}),
  ...(props.ariaSelected !== undefined ? { 'aria-selected': ternary(props.ariaSelected) } : {}),
  ...(props.ariaSetsize !== undefined ? { 'aria-setsize': props.ariaSetsize } : {})
} as const)
